import { useState, useMemo } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import dailyStoicData from "@/data/daily-stoic.json"

interface StoicEntry {
  date: string
  title: string
  quote: string
  attribution: string
  body: string
}

type DailyStoicData = Record<string, StoicEntry>

export function DailyStoicDialog() {
  const [open, setOpen] = useState(false)

  const { entry, displayDate } = useMemo(() => {
    const now = new Date()
    const month = now.getMonth() + 1
    const day = now.getDate()
    const key = `${month}-${day}`
    const data = dailyStoicData as DailyStoicData
    const entry = data[key] || null

    const displayDate = now.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })

    return { entry, displayDate }
  }, [])

  if (!entry) {
    return null
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="h-8 px-2 text-muted-foreground hover:bg-accent hover:text-foreground"
        title="Daily Stoic"
      >
        <span className="text-sm">{entry.title}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <p className="text-sm text-muted-foreground">{displayDate}</p>
            <DialogTitle className="text-xl mt-1">{entry.title}</DialogTitle>
            <DialogDescription className="sr-only">
              Daily Stoic meditation for {entry.date}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 pr-2 space-y-6">
            {/* Quote Section */}
            <blockquote className="border-l-4 border-primary/30 pl-4 py-2">
              <p className="text-base italic text-foreground/90 leading-relaxed">
                {entry.quote}
              </p>
              {entry.attribution && (
                <footer className="mt-2 text-sm text-muted-foreground">
                  {entry.attribution}
                </footer>
              )}
            </blockquote>

            {/* Body/Reflection */}
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <p className="text-sm leading-relaxed text-foreground/80 whitespace-pre-wrap">
                {entry.body}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
